import styled from '@emotion/styled';
import React, { memo } from 'react';
import { Row, Col, Typography } from 'antd';

import { OnChangeCallback } from '.';

import { Tooltip } from '../';
import theme from '../../theme';
import { IPersona } from '../../types/models/persona';

const { Paragraph } = Typography;

type CardClickEvent = { id: string; selected: boolean };

type OnClickCallback = (event: CardClickEvent) => void;

export type MultiSelectPhotosProps = {
  name: string;
  value?: string[];
  personas?: IPersona[];
  disabled?: boolean;
  onChange?: OnChangeCallback;
};

type PhotoItemProps = {
  id: string;
  name: string;
  tooltip: string;
  imageUrl: string;
  selected: boolean;
  onClick?: OnClickCallback;
};

const CARD_SIZE = 100;
const GUTTER = 16;
const SELECTED_CARD_OPACITY = 0.25;

const Image = styled.img<{ size: number }>(({ size }) => ({
  minWidth: size,
  minHeight: size,
  width: size,
  height: size,
  transition: `opacity 0.3s ease`,
}));

const TooltipIcon = styled(Tooltip)`
  position: absolute;
  right: 8px;
  bottom: 8px;
  background-color: #fff;
  border-radius: 50%;
`;

const ImageContainer = styled.div<{ size: number }>(
  {
    position: 'relative',
  },
  ({ size }) => ({
    width: size,
    height: size,
  })
);

const ParagraphStyle = styled(Paragraph)<{ selected: boolean }>(
  {
    fontSize: '12px',
    lineHeight: 1.5,
    padding: '4px 2px 0',
    height: '41px',
  },
  ({ selected }) => {
    if (selected) {
      return `
        color: ${theme.colorsBlack};
        font-weight: bold;
        border-left: 1px solid #d1d3d6;
        border-right: 1px solid #d1d3d6;
        border-bottom: 1px solid #d1d3d6;
      `;
    }
    return `
      color: ${theme.colorsSecondaryBlue};
      text-decoration: underline;
      border-bottom: 1px solid transparent;
    `;
  }
);

const CardWrapper = styled.div<{ size: number; selected: boolean; canSelect: boolean }>(
  {
    textAlign: 'center',
  },
  ({ size, selected, canSelect }) => {
    return `
      width: ${size}px;
      cursor: ${canSelect ? 'pointer' : 'auto'};

      ${Image} {
        opacity: ${selected ? 1 : SELECTED_CARD_OPACITY};
      }

      &:hover {
        ${Image} {
          opacity: 1;
        }
        ${ParagraphStyle} {
          font-weight: bold;
        }
      }
    `;
  }
);

const PhotoItem = memo(function PhotoItem({ id, name, tooltip, imageUrl, selected, onClick }: PhotoItemProps) {
  const cardClickHandler =
    onClick &&
    (() => {
      onClick({ id, selected: !selected });
    });

  return (
    <Col>
      <CardWrapper size={CARD_SIZE} selected={selected} onClick={cardClickHandler} canSelect={!!cardClickHandler}>
        <ImageContainer size={CARD_SIZE}>
          <Image src={imageUrl} alt={name} size={CARD_SIZE} />
          <TooltipIcon title={tooltip} />
        </ImageContainer>
        <ParagraphStyle ellipsis={{ rows: 2 }} selected={selected}>
          {name}
        </ParagraphStyle>
      </CardWrapper>
    </Col>
  );
});

const MultiSelectPhotos = memo(function MultiSelectPhotos({
  name,
  value,
  personas = [],
  disabled = false,
  onChange,
}: MultiSelectPhotosProps) {
  let cardClickHandler: OnClickCallback;
  if (onChange && !disabled) {
    cardClickHandler = ({ id, selected }: CardClickEvent) => {
      let updatedValue;
      if (selected) {
        updatedValue = Array.isArray(value) ? [...value, id] : [id];
      } else {
        updatedValue = value?.filter((itemId: string) => id !== itemId);
      }

      if (updatedValue && !updatedValue.length) {
        updatedValue = undefined;
      }

      onChange({ name, value: updatedValue });
    };
  }

  return (
    <Row gutter={[GUTTER, GUTTER]}>
      {personas.map(({ id, name, tooltip, imageUrl }) => {
        const isSelected = value?.includes(id) || false;
        return (
          <PhotoItem
            key={id}
            id={id}
            name={name}
            tooltip={tooltip}
            imageUrl={imageUrl}
            selected={isSelected}
            onClick={cardClickHandler}
          />
        );
      })}
    </Row>
  );
});

export default MultiSelectPhotos;
